import React, { useEffect, useState } from 'react';

import qs from 'qs';
import { FormattedMessage } from 'react-intl';
import { useSelector, useDispatch } from 'react-redux';

import { User } from 'src/components/types';
import { useIntl } from 'src/hooks/intl';
import fetchSearchResults from 'src/redux/search/actions/fetch-search-results';
import { SearchState } from 'src/redux/search/reducer';
import { Link } from 'src/router/components';
import { getCurrentRepositoryUuid } from 'src/selectors/repository-selectors';
import { getTargetUser } from 'src/selectors/user-selectors';
import { BucketState } from 'src/types/state';
import { publishScreenEvent } from 'src/utils/analytics/publish';

import { matchWrongSyntax } from '../../utils/extract-terms';
import { getPagingProps } from '../../utils/get-paging-props';

import messages from './results-source-view.i18n';
import * as styles from './results-source-view.style';
import Results from './search-results';

type ResultsSourceViewProps = {
  query?: string;
  resultsHeader: React.ComponentType<{
    message: React.ReactNode;
  }>;
};

export const ResultsSourceView: React.FC<ResultsSourceViewProps> = ({
  query,
  resultsHeader: ResultsHeader,
}) => {
  const dispatch = useDispatch();
  const [currentPage, setCurrentPage] = useState<number>();
  const intl = useIntl();
  const { response, isLoading } = useSelector<BucketState, SearchState>(
    state => state.search
  );
  const pagingProps = getPagingProps(response);
  const account = useSelector(getTargetUser);
  const repoUuid = useSelector(getCurrentRepositoryUuid);
  const queryWithRepoUuid = query ? `repo:${repoUuid} ${query}` : '';
  const syntaxError = matchWrongSyntax(queryWithRepoUuid);

  useEffect(() => {
    publishScreenEvent('repositorySourceSearchResultsScreen');
  }, []);

  const handleOnSetPage = (page: number) => {
    setCurrentPage(page);
  };

  const handleFetchSearchResults = (page?: number) => {
    if (queryWithRepoUuid) {
      dispatch(
        fetchSearchResults({
          account: account?.uuid,
          q: queryWithRepoUuid,
          page,
          extra: {
            // This will be always "false" as we are already checking
            // if there is a query.
            modifiersOnly: false,
            source: 'repositorySourceSearchResultsScreen',
          },
        })
      );
    }
  };

  // This hook is called each time when the query is changed,
  useEffect(() => {
    handleFetchSearchResults();
    // Intentionally added the below eslint disable rule,
    // we don't want the hook to be called when the current page changes.
    //
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [queryWithRepoUuid, account?.uuid, dispatch]);

  // Fetch the results with the new page.
  useEffect(() => {
    if (currentPage) {
      handleFetchSearchResults(currentPage);
    }
    // Intentionally added the below eslint disable rule,
    // we only want the hook to be executed when the current page changes.
    //
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage, account?.uuid, dispatch]);

  const resultsHeaderMessage = isLoading ? (
    intl.formatMessage(messages.loadingMessage)
  ) : (
    <>
      <b>
        {intl.formatMessage(messages.resultsCountHeader, {
          resultsCount: pagingProps.count,
        })}
      </b>{' '}
      <FormattedMessage
        {...messages.resultsInfoHeader}
        values={{
          codeSearchLink: (
            <Link
              to={`/search?${qs.stringify({
                account: account?.uuid,
                q: query,
              })}`}
            >
              {intl.formatMessage(messages.codeSearchLinkLabel)}
            </Link>
          ),
        }}
      />
    </>
  );

  return (
    <>
      <ResultsHeader message={resultsHeaderMessage} />
      <styles.Container>
        <Results
          isLoading={isLoading}
          response={response}
          count={pagingProps.count}
          currentPage={pagingProps.currentPage}
          showPages={pagingProps.showPages}
          pages={pagingProps.pages}
          account={account as User}
          onSetPage={handleOnSetPage}
          syntaxError={syntaxError}
          query={query || ''}
        />
      </styles.Container>
    </>
  );
};
